import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/pushSubscription";

/**
 * Toggle strip that lets counsellors enable/disable browser push notifications.
 *
 * Renders three visual states so a counsellor always knows where they stand:
 *   1. `unsupported` — browser can't do Web Push (e.g. iOS Safari before 16.4). Grey card.
 *   2. `denied`     — permission was blocked at the browser/OS level. Show instructions.
 *   3. `off` / `on` — the normal happy path with a call-to-action button.
 */
export default function PushToggle() {
  const [state, setState] = useState("loading"); // loading | unsupported | denied | on | off
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pushSupported()) { if (!cancelled) setState("unsupported"); return; }
      if (Notification.permission === "denied") { if (!cancelled) setState("denied"); return; }
      const sub = await getCurrentSubscription();
      if (!cancelled) setState(sub ? "on" : "off");
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) { setState("on"); toast.success("Browser notifications turned on"); return; }
    if (res.reason === "denied") { setState("denied"); toast.error("Notifications are blocked in your browser settings"); return; }
    if (res.reason === "unsupported") { setState("unsupported"); toast.error("This browser doesn't support push notifications"); return; }
    if (res.reason === "dismissed") { toast("Notification permission dismissed"); return; }
    toast.error("Could not enable notifications");
  };

  const disable = async () => {
    setBusy(true);
    const res = await unsubscribeFromPush();
    setBusy(false);
    if (res.ok) { setState("off"); toast.success("Browser notifications turned off"); }
    else toast.error("Could not turn off notifications");
  };

  if (state === "loading") return null;

  if (state === "unsupported") {
    return (
      <div className="brand-card p-4 mb-4 flex items-start gap-3 opacity-80" data-testid="push-toggle-unsupported">
        <BellOff size={18} className="text-[#7A7D96] mt-0.5" />
        <div className="text-xs text-[#5F627D]">
          <span className="font-bold text-[#3D405B]">Browser notifications unavailable.</span> Your device/browser
          doesn&apos;t support Web Push. You&apos;ll still get email alerts and the in-app toast.
        </div>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="brand-card p-4 mb-4 flex items-start gap-3" data-testid="push-toggle-denied">
        <BellOff size={18} className="text-destructive mt-0.5" />
        <div className="text-xs text-[#5F627D]">
          <span className="font-bold text-[#3D405B]">Notifications are blocked.</span> To re-enable, click the padlock
          icon in your browser&apos;s address bar and allow notifications for this site.
        </div>
      </div>
    );
  }

  const isOn = state === "on";
  return (
    <div
      className={`brand-card p-4 mb-4 flex items-center justify-between gap-3 ${isOn ? "border-secondary/40" : ""}`}
      data-testid="push-toggle"
      data-state={state}
    >
      <div className="flex items-start gap-3">
        {isOn ? <Bell size={18} className="text-secondary mt-0.5" /> : <BellOff size={18} className="text-[#7A7D96] mt-0.5" />}
        <div>
          <div className="text-sm font-extrabold text-[#3D405B]">
            {isOn ? "Browser notifications on" : "Get real-time alerts"}
          </div>
          <div className="text-xs text-[#7A7D96] mt-0.5">
            {isOn
              ? "You'll be pinged the moment a client waits, books, or messages you — even with the app closed."
              : "Turn on browser notifications so we can ping you the moment a client waits, books, or messages you."}
          </div>
        </div>
      </div>
      <button
        onClick={isOn ? disable : enable}
        disabled={busy}
        className={`px-4 py-2 rounded-full text-sm font-extrabold shrink-0 transition-all shadow-sm ${
          isOn
            ? "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"
            : "bg-secondary text-white hover:-translate-y-0.5"
        }`}
        data-testid={isOn ? "push-disable-btn" : "push-enable-btn"}
      >
        {busy ? "…" : isOn ? "Turn off" : "Enable"}
      </button>
    </div>
  );
}
